// The one place the app decides "ours vs library" for a template's send panel. Flip a LOCAL_SEND key to false, then
// delete its folder, to hand that template type back to @gs/gs-integration-component's own TemplateMessaging once it
// can render it; see README.md for the full swap-out steps.
import { CarouselSendPanel } from './carousel';
import { StandardSendPanel } from './standard';
import { isLocalFormSupported } from './standard/isLocalFormSupported';
import { LOCAL_SEND } from './shared/localSend';
import { describeTemplate } from './shared/templateModel';

export { LOCAL_SEND };

/**
 * @typedef {object} LocalSendMatch
 * @property {string} key - a LOCAL_SEND key, e.g. 'carousel' | 'standard'
 * @property {import('react').ComponentType<any>} Panel
 */

/**
 * Resolves which local send panel (if any) should render for a picked/restored template.
 * Carousel first (unchanged) — everything else GIF/LOCATION/LTO/voice-call resolves to the one
 * Standard panel, which reads describeTemplate() itself to shape its own fields.
 * `allTemplates` (the host's own-form flag) adds every other template the Standard panel can send
 * (isLocalFormSupported); the rest, e.g. CATALOG or FLOW, stay on the shared component's form. It only ever adds
 * matches, so a template that resolves without it resolves with it.
 * @param {{ details?: object }} template - mapTemplate() shape
 * @param {{ allTemplates?: boolean }} [options]
 * @returns {LocalSendMatch|null}
 */
export function resolveLocalSendPanel(template, { allTemplates } = {}) {
  const details = template?.details;
  if (LOCAL_SEND.carousel
    && details?.button_type === 'CAROUSEL'
    && Array.isArray(details?.carouselCards)
    && details.carouselCards.length) {
    return { key: 'carousel', Panel: CarouselSendPanel };
  }

  const describe = describeTemplate(details);
  if ((LOCAL_SEND.gif && describe.headerKind === 'GIF')
    || (LOCAL_SEND.location && describe.headerKind === 'LOCATION')
    || (LOCAL_SEND.lto && describe.isLto)
    || (LOCAL_SEND.voiceCall && describe.hasVoiceCall)
    || (allTemplates && isLocalFormSupported(details))) {
    return { key: 'standard', Panel: StandardSendPanel };
  }

  return null;
}
